import React from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Button,
    Box,
    Divider,
    Chip,
    Tooltip,
    Alert,
    Checkbox
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MonitorPlay, FileText, ClipboardList, Award, ExternalLink } from 'lucide-react';
import { generateCertificatePDF } from './CertificateGenerator';

const backgroundColor = '#f9f9f9';

const commonStyles = {
    fontFamily: 'var(--font-stack-text)',
    fontSize: { xs: '1rem', sm: '1.1rem' },
};

const itemHoverEffect = {
    cursor: "pointer",
    backgroundColor: "#fff",
    px: 2,
    py: 1.2,
    borderRadius: '8px',
    mb: 0.7,
    transition: 'all 0.25s ease-in-out',
    '&:hover': {
        boxShadow: '0px 2px 12px rgba(0,0,0,0.05)',
        backgroundColor: '#f1fdf7',
        transform: 'translateY(-1px)',
    }
};

const quizItemHoverEffect = {
    ...itemHoverEffect,
    '&:hover': {
        boxShadow: '0px 3px 15px rgba(255, 152, 0, 0.15)',
        backgroundColor: '#fff8e1',
        transform: 'translateY(-1px)',
    }
};

const quizItemDisabledEffect = {
    cursor: "not-allowed",
    backgroundColor: "#f5f5f5",
    px: 2,
    py: 1.2,
    borderRadius: '8px',
    mb: 0.7,
    opacity: 0.6,
};

const CourseAccordion = ({
    videos = [],
    documents = [],
    questionnaires = [],
    onSelectVideo,
    onCertificateClick,
    onQuizClick, // Nueva prop para manejar click en cuestionarios
    courseProgress = 0, // Progreso del curso (0-100)
    userName = '', // Nombre del usuario logueado
    userCedula = '', // Cédula del usuario (para el certificado)
    courseTitle = '', // Título del curso
    isPreviewMode = false, // Vista de validación admin: sin candado de progreso
}) => {
    
    const areQuizzesEnabled = isPreviewMode || courseProgress >= 95;
    const isCertificateEnabled = isPreviewMode || courseProgress >= 100;
    
    // Función para abrir cuestionario en nueva pestaña
    const handleQuizClick = (quizId, quizTitle) => {
        if (!areQuizzesEnabled) {
            return; // No hacer nada si los cuestionarios están deshabilitados
        }
        
        if (onQuizClick) {
            onQuizClick(quizId, quizTitle);
        } else {
            // Fallback: abrir en nueva pestaña directamente
            const quizUrl = `/courses/millionaire-game/${quizId}`;
            window.open(quizUrl, '_blank', 'noopener,noreferrer');
        }
    };
    
    // Función para generar el certificado en PDF
    const handleGenerateCertificate = async () => {
        if (!isCertificateEnabled) {
            return;
        }

        const certificateName = userName || (isPreviewMode ? 'Vista previa' : '');
        if (!certificateName) {
            return;
        }
        
        try {
            await generateCertificatePDF(certificateName, courseTitle, userCedula);
        } catch (e) {
            console.error('Error al generar el certificado:', e);
        }
    };

    // Función para obtener el color del chip según el número de preguntas
    const getQuestionChipColor = (questionCount) => {
        if (questionCount <= 5) return 'success';
        if (questionCount <= 10) return 'warning';
        return 'error';
    };

    // Función para obtener texto descriptivo según el número de preguntas
    const getDifficultyText = (questionCount) => {
        if (questionCount <= 5) return 'Fácil';
        if (questionCount <= 10) return 'Intermedio';
        return 'Avanzado';
    };

    return (
        <Box mt={3}>
            {/* Videos */}
            <Accordion
                defaultExpanded
                sx={{
                    backgroundColor,
                    borderRadius: 3,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                    mb: 2,
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    sx={{ 
                        '&:hover': { backgroundColor: 'rgba(67, 160, 71, 0.05)' },
                        transition: 'background-color 0.2s ease'
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1} width="100%">
                        <MonitorPlay size={22} color="white" />
                        <Typography sx={{ fontWeight: 'bold', ...commonStyles }}>
                            VIDEOS DEL CURSO
                        </Typography>
                        <Chip 
                            label={videos.length} 
                            size="small" 
                            color="success" 
                            sx={{ ml: 1, fontSize: '0.75rem' }}
                        />
                        <Box sx={{ ml: 'auto' }}>
                            <Checkbox
                                checked={videos.length > 0 && videos.every(video => video.isWatched === true)}
                                indeterminate={videos.length > 0 && videos.some(video => video.isWatched === true) && !videos.every(video => video.isWatched === true)}
                                disabled
                                icon={<Box sx={{ width: 20, height: 20, border: '2px solid white', borderRadius: '4px' }} />}
                                checkedIcon={<CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />}
                                sx={{ 
                                    p: 0,
                                    color: 'white',
                                    '&.Mui-checked': {
                                        color: 'white'
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </AccordionSummary>
                <Divider />
                {videos.length > 0 ? (
                    videos.map((video, index) => (
                        <AccordionDetails
                            key={index}
                            sx={itemHoverEffect}
                            onClick={() => onSelectVideo(video.path, index)}
                        >
                            <Box display="flex" alignItems="center" gap={2} width="100%">
                                <MonitorPlay size={18} color="#43a047" />
                                <Typography sx={{ fontSize: '0.95rem', flex: 1 }}>
                                    {`${index + 1}. ${video.title}`}
                                </Typography>
                                {video.duration && (
                                    <Chip 
                                        label={video.duration} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                )}
                                <Checkbox
                                    checked={video.isWatched === true}
                                    disabled
                                    icon={<Box sx={{ width: 20, height: 20, border: '2px solid #ccc', borderRadius: '4px' }} />}
                                    checkedIcon={<CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />}
                                    sx={{ 
                                        p: 0,
                                        '&.Mui-checked': {
                                            color: '#4caf50'
                                        }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </Box>
                        </AccordionDetails>
                    ))
                ) : (
                    <AccordionDetails sx={{ px: 2, py: 1 }}>
                        <Typography sx={{ fontSize: '0.95rem', color: '#666' }}>
                            No hay videos disponibles
                        </Typography>
                    </AccordionDetails>
                )}
            </Accordion>

            {/* Documentos */}
            <Accordion
                sx={{
                    backgroundColor,
                    borderRadius: 3,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                    mb: 2,
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    sx={{ 
                        '&:hover': { backgroundColor: 'rgba(30, 136, 229, 0.05)' },
                        transition: 'background-color 0.2s ease'
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        <FileText size={22} color="white" />
                        <Typography sx={{ fontWeight: 'bold', ...commonStyles }}>
                            MATERIAL DEL CURSO
                        </Typography>
                        <Chip 
                            label={documents.length} 
                            size="small" 
                            color="primary" 
                            sx={{ ml: 1, fontSize: '0.75rem' }}
                        />
                    </Box>
                </AccordionSummary>
                <Divider />
                {documents.length > 0 ? (
                    documents.map((doc, index) => (
                        <AccordionDetails
                            key={index}
                            sx={itemHoverEffect}
                            onClick={() => window.open(`https://docentia.com.co/backend/${doc.path}`, "_blank")}
                        >
                            <Box display="flex" alignItems="center" gap={2} width="100%">
                                <FileText size={18} color="#1e88e5" />
                                <Typography sx={{ fontSize: '0.95rem', flex: 1 }}>
                                    {`${index + 1}. ${doc.title}`}
                                </Typography>
                                <ExternalLink size={16} color="#666" />
                            </Box>
                        </AccordionDetails>
                    ))
                ) : (
                    <AccordionDetails sx={{ px: 2, py: 1 }}>
                        <Typography sx={{ fontSize: '0.95rem', color: '#666' }}>
                            No hay documentos disponibles
                        </Typography>
                    </AccordionDetails>
                )}
            </Accordion>

            {/* Cuestionarios */}
            <Accordion
                sx={{
                    backgroundColor,
                    borderRadius: 3,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                    mb: 2,
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    sx={{ 
                        '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.05)' },
                        transition: 'background-color 0.2s ease'
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        <ClipboardList size={22} color="white" />
                        <Typography sx={{ fontWeight: 'bold', ...commonStyles }}>
                            ¿QUIÉN QUIERE SER MILLONARIO?
                        </Typography>
                        <Chip 
                            label={questionnaires.length} 
                            size="small" 
                            color="warning" 
                            sx={{ ml: 1, fontSize: '0.75rem' }}
                        />
                    </Box>
                </AccordionSummary>
                <Divider />
                {isPreviewMode && (
                    <AccordionDetails sx={{ px: 2, py: 1 }}>
                        <Alert severity="info" sx={{ mb: 1 }}>
                            Vista previa: puedes validar los cuestionarios sin completar el progreso del curso.
                        </Alert>
                    </AccordionDetails>
                )}
                {!areQuizzesEnabled && (
                    <AccordionDetails sx={{ px: 2, py: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Los cuestionarios estarán disponibles cuando completes al menos el 95% del curso.
                            Progreso actual: {courseProgress.toFixed(1)}%
                        </Alert>
                    </AccordionDetails>
                )}
                {questionnaires.length > 0 ? (
                    questionnaires.map((quiz, index) => (
                        <AccordionDetails 
                            key={index} 
                            sx={areQuizzesEnabled ? quizItemHoverEffect : quizItemDisabledEffect}
                            onClick={() => areQuizzesEnabled && handleQuizClick(quiz.id, quiz.title)}
                        >
                            <Box display="flex" flexDirection="column" width="100%">
                                <Box display="flex" alignItems="center" gap={2} mb={1}>
                                    <ClipboardList size={18} color={areQuizzesEnabled ? "#ff9800" : "#ccc"} />
                                    <Typography sx={{ 
                                        fontSize: '0.95rem', 
                                        fontWeight: '500', 
                                        flex: 1,
                                        color: areQuizzesEnabled ? 'inherit' : '#999'
                                    }}>
                                        {quiz.title}
                                    </Typography>
                                    <Tooltip title={`Nivel: ${getDifficultyText(quiz.questions?.length || 0)}`}>
                                        <Chip 
                                            label={`${quiz.questions?.length || 0} preguntas`}
                                            size="small"
                                            color={areQuizzesEnabled ? getQuestionChipColor(quiz.questions.length) : 'default'}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    </Tooltip>
                                </Box>
                                
                                {areQuizzesEnabled && (
                                    <>
                                        <Box display="flex" alignItems="center" gap={2} ml={3}>
                                            <Typography sx={{ 
                                                fontSize: '0.8rem', 
                                                color: '#666',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}>
                                                🎯 Juega "¿Quién quiere ser millonario?"
                                            </Typography>
                                            <ExternalLink size={14} color="#ff9800" />
                                        </Box>
                                        
                                        {quiz.description && (
                                            <Typography sx={{ 
                                                fontSize: '0.8rem', 
                                                color: '#888', 
                                                ml: 3,
                                                mt: 0.5,
                                                fontStyle: 'italic'
                                            }}>
                                                {quiz.description}
                                            </Typography>
                                        )}
                                    </>
                                )}
                                {!areQuizzesEnabled && (
                                    <Typography sx={{ 
                                        fontSize: '0.8rem', 
                                        color: '#999',
                                        ml: 3,
                                        mt: 0.5,
                                        fontStyle: 'italic'
                                    }}>
                                        Completa el 95% del curso para acceder a este cuestionario
                                    </Typography>
                                )}
                            </Box>
                        </AccordionDetails>
                    ))
                ) : (
                    <AccordionDetails sx={{ px: 2, py: 1 }}>
                        <Typography sx={{ fontSize: '0.95rem', color: '#666' }}>
                            No hay cuestionarios disponibles
                        </Typography>
                    </AccordionDetails>
                )}
            </Accordion>

            {/* Botón de Certificado */}
            <Box textAlign="center" mt={4}>
                {isPreviewMode && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Vista previa: puedes generar el certificado para validar el diseño, sin exigir el 100% de progreso.
                    </Alert>
                )}
                {!isCertificateEnabled && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Completa el 100% del curso para obtener tu certificado.
                        Progreso actual: {courseProgress.toFixed(1)}%
                    </Alert>
                )}
                <Button
                    variant="contained"
                    color="success"
                    onClick={isCertificateEnabled ? handleGenerateCertificate : undefined}
                    disabled={!isCertificateEnabled}
                    startIcon={<Award size={20} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        px: 5,
                        py: 1.5,
                        borderRadius: '999px',
                        boxShadow: isCertificateEnabled ? '0px 4px 14px rgba(67, 160, 71, 0.3)' : 'none',
                        background: isCertificateEnabled 
                            ? 'linear-gradient(45deg, #43a047 30%, #66bb6a 90%)'
                            : 'linear-gradient(45deg, #cccccc 30%, #999999 90%)',
                        '&:hover': isCertificateEnabled ? {
                            backgroundColor: '#388e3c',
                            boxShadow: '0px 6px 18px rgba(67, 160, 71, 0.4)',
                            transform: 'translateY(-2px)',
                        } : {},
                        '&:disabled': {
                            background: 'linear-gradient(45deg, #cccccc 30%, #999999 90%)',
                            color: '#666',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Obtener Certificado
                </Button>
            </Box>
        </Box>
    );
};

export default CourseAccordion;